<template>
  <sfs-base-table
    :key="tableKey"
    :data-provider="$workOrderChargesAPI.getWorkOrderCharges"
    :data-id="workOrderId"
    provider-key="workOrderCharges"
    :columns="tableColumns"
    :filters="chargeFilters"
    :current-page="currentPage"
    :per-page="perPage"
    :show-per-page="showPerPage"
    :append-to-query="false"
    :skeleton-rows="2"
    @data-changed="onDataChanged"
    @total-rows-changed="(totalRows) => $emit('total-rows-changed', totalRows)"
  >
    <template #cell(service_id)="data">
      <b-link
        v-if="data.item.service_id"
        :to="{ name: 'service', params: { id: data.item.service_id } }"
      >
        {{ data.item.service.name }}
      </b-link>
    </template>
    <template #cell(user_id)="data">
      <b-link
        v-if="data.item.user_id"
        :to="{ name: 'user', params: { id: data.item.user_id } }"
      >
        {{ data.item.user.name }}
      </b-link>
      <div
        v-if="data.item.pay_technician_immediately"
        class="font-size-sm mt-1 text-secondary"
      >
        <mark v-b-popover.hover.top="'This charge will be billed even if the work order is not yet completed'">
          Paid & Invoiced next billing cycle
        </mark>
      </div>
    </template>
    <template #cell(description)="data">
      <text-with-popover
        v-if="data.item.description != data.item.service?.name"
        :popover-id="`work-order-charge-${data.item.id}-description`"
        :text="data.item.description"
      />
      <b-link
        v-if="data.item.service_id"
        class="font-size-sm text-prewrap"
        :to="{ name: 'service', params: { id: data.item.service_id } }"
      >
        {{ data.item.service.name }}
      </b-link>
    </template>
    <template #cell(category)="data">
      <b-badge
        class="font-size-sm"
        variant="secondary"
      >
        {{ data.item.category | humanizedFormat | capitalizeFormat }}
      </b-badge>
    </template>
    <template #cell(cost)="data">
      <money-format
        :value="data.item.cost.amount"
        :currency-code="data.item.cost.currency"
        subunits-value
      />
      <money-format
        v-if="$can('read_price', 'WorkOrderCharge') && data.item.cost.amount != data.item.total_cost.amount"
        class="font-size-sm text-secondary"
        :value="data.item.total_cost.amount"
        :currency-code="data.item.total_cost.currency"
        subunits-value
      />
    </template>

    <template #cell(price)="data">
      <money-format
        v-if="$can('read_price', 'WorkOrderCharge')"
        :value="data.item.price.amount"
        :currency-code="data.item.price.currency"
        subunits-value
      />
      <money-format
        v-if="$can('read_price', 'WorkOrderCharge') && data.item.price.amount != data.item.total_price.amount"
        class="font-size-sm text-secondary"
        :value="data.item.total_price.amount"
        :currency-code="data.item.total_price.currency"
        subunits-value
      />
    </template>

    <template #cell(taxes)="data">
      <div
        v-if="$can('read_price', 'WorkOrderCharge')"
        class="text-nowrap tax-rate-info"
      >
        <div v-if="taxAmounts[data.item.id] > 0">
          <money-format
            :id="`tax-rate-info-${data.item.id}`"
            class="d-inline"
            :value="taxAmounts[data.item.id]"
            :currency-code="data.item.price.currency"
            subunits-value
          />
          <tax-rate-popover
            :target="`tax-rate-info-${data.item.id}`"
            :tax-rates="[taxRates[data.item.id]]"
          />
        </div>
        <div v-else-if="taxAmounts[data.item.id] === 0" />
        <div v-else>
          <b-skeleton />
        </div>
      </div>
    </template>

    <template #cell(actions)="data">
      <div class="text-nowrap">
        <b-button
          v-if="$can('update', data.item)"
          v-b-modal="`work-order-charge-${data.item.id}-edit-modal`"
          size="sm"
          variant="link mr-1"
        >
          <i class="fa fa-fw fa-pencil-alt" />
        </b-button>
        <b-dropdown
          v-if="$can('destroy', data.item) || $can('read', 'Version')"
          variant="link"
          size="sm"
          boundary="window"
          no-caret
        >
          <template #button-content>
            <i class="fas fa-ellipsis-h" />
          </template>
          <b-dropdown-item
            v-if="$can('destroy', data.item)"
            size="sm"
            @click="destroyWorkOrderChargeDialog(data.item.id)"
          >
            <i class="fa fa-fw fa-times mr-1" />
            Delete
          </b-dropdown-item>
          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: data.item.id, model: 'WorkOrderCharge' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <edit-modal
        v-if="$can('update', data.item)"
        :key="`work-order-charges-edit-modal-${data.item.id}`"
        :value="data.item"
        :work-order="workOrder"
        @on-save="refreshTable"
      />
    </template>

    <template #custom-foot>
      <b-tr
        v-if="charges.length"
        class="font-w600"
      >
        <b-td
          v-for="column in tableColumns"
          :key="`foot-${column.key}`"
        >
          <template v-if="column.key === 'description'">
            Total
          </template>
          <money-format
            v-else-if="column.key === 'cost'"
            :value="totalCostAmount"
            :currency-code="currencyCode"
            subunits-value
          />
          <money-format
            v-else-if="column.key === 'price' && $can('read_price', 'WorkOrderCharge')"
            :value="totalPriceAmount"
            :currency-code="currencyCode"
            subunits-value
          />
        </b-td>
      </b-tr>
    </template>
  </sfs-base-table>
</template>

<script>
import tablePropsMixin from '@/mixins/tablePropsMixin';
import translationMixin from '@/mixins/translationMixin';
import { WORK_ORDER_CHARGES_TABLE_DEFAULT_COLUMNS } from '@/constants/workOrderCharges';
import { WORK_ORDER_MODEL_NAME } from '@/constants/workOrders';
import EditModal from '@/components/admin/workOrderCharges/EditModal.vue';
import TaxRatePopover from '@/components/admin/taxRates/TaxRatePopover.vue';
import TextWithPopover from '@/components/shared/TextWithPopover.vue';

export default {
  name: 'WorkOrderChargesTable',
  mixins: [tablePropsMixin, translationMixin],
  components: {
    EditModal,
    TaxRatePopover,
    TextWithPopover,
  },
  props: {
    workOrderId: {
      type: Number,
      required: true,
    },
    workOrder: Object,
    columns: {
      type: Array,
      default: () => WORK_ORDER_CHARGES_TABLE_DEFAULT_COLUMNS,
    },
  },
  data() {
    return {
      tableKey: Date.now(),
      charges: [],
      taxAmounts: {},
      taxRates: {},
      totalRows: 0,
      modelName: WORK_ORDER_MODEL_NAME,
    };
  },
  computed: {
    tableColumns() {
      return this.translatedColumns(this.modelName, this.columns);
    },
    chargeFilters() {
      return { paginate: false, ...this.filters };
    },
    totalCostAmount() {
      return this.charges.reduce((sum, charge) => sum + (charge.total_cost?.amount || 0), 0);
    },
    totalPriceAmount() {
      return this.charges.reduce((sum, charge) => sum + (charge.total_price?.amount || 0), 0);
    },
    currencyCode() {
      return this.charges[0]?.cost?.currency || this.charges[0]?.price?.currency;
    },
  },
  methods: {
    destroyWorkOrderChargeDialog(id) {
      this.$swal({
        text: 'Once it\'s deleted, its gone forever!',
        preConfirm: () => this.$workOrderChargesAPI.destroy(id),
      }).then((result) => {
        if (result.value) {
          this.refreshTable();
        }
        result.timer;
      });
    },
    onDataChanged(records) {
      this.charges = records || [];
      this.charges.forEach((item) => this.calculateEstimatedTaxes(item));
    },
    calculateEstimatedTaxes(charge) {
      const id = charge.id;
      this.$taxRatesAPI.getWorkOrderChargeTaxRates(id, {})
        .then((response) => {
          const { total, taxRates } = response;
          if (total == 0) {
            this.taxAmounts[id] = 0;
          } else {
            const taxRate = taxRates[0];
            this.taxAmounts[id] = (charge.quantity * charge.price.amount) * taxRate.rate;
            this.taxRates[id] = taxRate;
          }
          this.taxAmounts = Object.assign({}, this.taxAmounts);
          this.taxRates = Object.assign({}, this.taxRates);
        });
    },
  },
};
</script>
